import { h, Fragment } from 'preact';
import { useState, useEffect, useMemo } from 'preact/hooks';
import AppointmentScheduler from '../../components/Scheduler/Scheduler';
import FormStepLayout from '../../templates/FormStepLayout/FormStepLayout';
import RightSidebar from '../../components/sidebars/RightSidebar';
import Loader from '../../components/Loader';
import Alert from '../../components/Alert';
import useFormStore from '../../store/useFormStore';

export default function SelectSchedule({ franchise, onBack, onNext }) {
  const [serviceSchedulers, setServiceSchedulers] = useState([]);
  const [schedulingRequired, setSchedulingRequired] = useState(false);
  const [daysCount, setDaysCount] = useState(30);
  const [unavailabilityWarning, setUnavailabilityWarning] = useState(null);

  // Helper function to parse date string in local time (not UTC)
  // This prevents timezone issues where "2026-01-27" becomes Jan 26 in PST
  const parseDateString = (dateString) => {
    const [year, month, day] = dateString.split('-').map(Number);
    return new Date(year, month - 1, day);
  };

  // Start date will be calculated based on first available slot
  const [schedulerStartDate, setSchedulerStartDate] = useState(new Date());

  // Determine days to show based on screen size
  const [visibleDays, setVisibleDays] = useState(7);

  useEffect(() => {
    const updateVisibleDays = () => {
      if (window.innerWidth <= 480) {
        setVisibleDays(3);
      } else if (window.innerWidth <= 768) {
        setVisibleDays(5);
      } else {
        setVisibleDays(7);
      }
    };

    updateVisibleDays();
    window.addEventListener('resize', updateVisibleDays);
    return () => window.removeEventListener('resize', updateVisibleDays);
  }, []);

  // Get data from Zustand store
  const currentFranchise = useFormStore((state) => state.currentFranchise);
  const currentPostalCode = useFormStore((state) => state.currentPostalCode);
  const selectedServices = useFormStore((state) => state.selectedServices);
  const selectedEstimateType = useFormStore((state) => state.selectedEstimateType);
  const setIsTransitioning = useFormStore((state) => state.setIsTransitioning);
  const isTransitioning = useFormStore((state) => state.isTransitioning);
  const getServiceById = useFormStore((state) => state.getServiceById);

  // Appointment store functions
  const scheduledAppointments = useFormStore((state) => state.scheduledAppointments);
  const setAppointmentForService = useFormStore((state) => state.setAppointmentForService);
  const removeAppointmentForService = useFormStore((state) => state.removeAppointmentForService);
  const clearAllAppointments = useFormStore((state) => state.clearAllAppointments);

  // Cleanup: Reset isTransitioning when component unmounts
  useEffect(() => {
    return () => {
      setIsTransitioning(false);
    };
  }, []);

  useEffect(() => {
    // Clean up any old appointments that don't match the new structure
    if (scheduledAppointments && scheduledAppointments.length > 0) {
      const hasInvalidAppointments = scheduledAppointments.some(appt => {
        // Check if appointment has the new structure (serviceIds array, services object, and timeslot object)
        return !appt.serviceIds || !Array.isArray(appt.serviceIds) || !appt.services || !appt.timeslot || typeof appt.services !== 'object';
      });

      // If any appointments have old structure, clear all and start fresh
      if (hasInvalidAppointments) {
        console.warn('Detected old appointment data structure. Clearing all appointments.');
        clearAllAppointments();
      }
    }
  }, []);

  // Validate previously selected appointments against new availability
  const validatePreviouslySelectedAppointments = (schedulers, requestedDays) => {
    if (!scheduledAppointments || scheduledAppointments.length === 0) {
      return; // No previous appointments to validate
    }

    // Calculate the date range covered by the API response
    const today = new Date();
    const endDate = new Date(today);
    endDate.setDate(today.getDate() + requestedDays - 1);
    const todayString = formatDateLocal(today);
    const endDateString = formatDateLocal(endDate);

    const unavailableAppointments = [];

    scheduledAppointments.forEach(appointment => {
      // Safety check: ensure appointment has new structure
      if (!appointment.serviceIds || !Array.isArray(appointment.serviceIds) || !appointment.services || !appointment.timeslot || typeof appointment.services !== 'object') {
        unavailableAppointments.push(appointment);
        return;
      }

      // Only validate appointments that fall within the API's date range
      // Appointments beyond the requested date range are kept as-is
      if (appointment.timeslot.date < todayString || appointment.timeslot.date > endDateString) {
        return; // Skip validation for appointments outside the API date range
      }

      // Get service IDs from the appointment's serviceIds array
      const appointmentServiceIds = appointment.serviceIds.map(id => String(id));

      // Find the scheduler that contains these services
      const scheduler = schedulers.find(s =>
        s.serviceIds.some(id => appointmentServiceIds.includes(String(id)))
      );

      if (!scheduler) {
        // Services not found in new data
        unavailableAppointments.push(appointment);
        return;
      }

      // Check if the slot is still available
      const slotKey = `${appointment.timeslot.date}:${appointment.timeslot.timeslot}`;
      if (!scheduler.available.includes(slotKey)) {
        unavailableAppointments.push(appointment);
      }
    });

    // If any appointments are no longer available, notify user and clear them
    if (unavailableAppointments.length > 0) {
      console.warn('Previously selected appointments are no longer available:', unavailableAppointments);

      // Remove unavailable appointments from store
      unavailableAppointments.forEach(appt => {
        // Try to construct an appointment key from whatever data is available
        let appointmentKey = null;

        if (appt.serviceIds && Array.isArray(appt.serviceIds)) {
          // New structure: use sorted service IDs from array
          appointmentKey = [...appt.serviceIds].sort().join('-');
        } else if (appt.services && typeof appt.services === 'object') {
          // Fallback: use sorted service IDs from services object keys
          appointmentKey = Object.keys(appt.services).sort().join('-');
        } else if (appt.serviceId) {
          // Old structure: use serviceId string
          appointmentKey = appt.serviceId;
        }

        if (appointmentKey) {
          removeAppointmentForService(appointmentKey);
        }
      });

      // Show user notification via banner
      setUnavailabilityWarning(
        `Your previously selected appointment time${unavailableAppointments.length > 1 ? 's are' : ' is'} no longer available. Please select new times.`
      );
    }
  };

  // Fetch schedule timeslots from the API
  useEffect(() => {
    let cancelled = false;

    const fetchScheduleTimeslots = async () => {
      // Validate required data
      if (!currentFranchise?.id || !selectedServices.length) {
        console.warn('Missing required data for schedule API:', {
          franchise: currentFranchise,
          services: selectedServices
        });
        setIsTransitioning(false);
        return;
      }

      setIsTransitioning(true);

      try {
        // First, fetch the CSRF token
        const csrfResponse = await fetch('/session/token');
        const csrfToken = await csrfResponse.text();

        const franchiseId = currentFranchise.id;
        const postalCode = currentPostalCode || currentFranchise.address?.postal_code;

        if (!postalCode) {
          setIsTransitioning(false);
          return;
        }

        // Map estimate type for API: 'on_site_estimate' -> 'quote', 'on_site_estimate_and_perform_services' -> 'schedule'
        const serviceType = selectedEstimateType === 'on_site_estimate' ? 'quote' : 'schedule';

        // Filter out message-type services (they don't require scheduling)
        // Only include services with type "table" or undefined (configurable services)
        const serviceIds = selectedServices.filter(serviceId => {
          const serviceConfig = getServiceById(serviceId)?.form;
          return serviceConfig?.type !== 'message';
        });

        // If no services require scheduling, skip the API call
        if (serviceIds.length === 0) {
          setServiceSchedulers([]);
          setSchedulingRequired(false);
          setIsTransitioning(false);
          return;
        }

        // These services require scheduling, so at least one appointment must
        // be selected before the user can proceed.
        setSchedulingRequired(true);

        const requestedDays = 30;
        const requestData = {
          service_type: serviceType,
          services: serviceIds,
          start: new Date().toISOString().split('T')[0], // Today's date in YYYY-MM-DD format
          days: requestedDays
        };

        const response = await fetch(`/api/schedule/${franchiseId}/${postalCode}`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-CSRF-Token': csrfToken,
          },
          body: JSON.stringify(requestData)
        });

        const result = await response.json();

        if (cancelled) {
          return;
        }

        if (result.success && result.timeslots) {
          // Transform the API data for each service
          const schedulers = transformTimeslotDataForServices(result.timeslots, requestedDays);
          setServiceSchedulers(schedulers);
          setDaysCount(requestedDays);

          // Find the earliest available date across all schedulers
          let earliestDate = null;
          schedulers.forEach(scheduler => {
            if (scheduler.available.length > 0) {
              const firstSlot = scheduler.available[0];
              const dateString = firstSlot.split(':')[0];
              const slotDate = parseDateString(dateString);
              if (!earliestDate || slotDate < earliestDate) {
                earliestDate = slotDate;
              }
            }
          });

          // Set the scheduler start date to the earliest available date (or today if none found)
          if (earliestDate) {
            setSchedulerStartDate(earliestDate);
          }

          // Validate previously selected appointments against new availability
          validatePreviouslySelectedAppointments(schedulers, requestedDays);
        } else {
          console.error('Failed to fetch timeslots:', result.message || 'Unknown error');
        }
      } catch (error) {
        console.error('Error fetching schedule timeslots:', error);
      } finally {
        if (!cancelled) {
          setIsTransitioning(false);
        }
      }
    };

    fetchScheduleTimeslots();

    return () => {
      cancelled = true;
    };
  }, [currentFranchise, currentPostalCode, selectedServices, selectedEstimateType]);

  // The API returns availability for `daysCount` days starting today, so the
  // last selectable date is today + (daysCount - 1). Anything past this window
  // has no availability data and must not be selectable.
  const maxSelectableDate = useMemo(() => {
    const today = new Date();
    const last = new Date(today.getFullYear(), today.getMonth(), today.getDate());
    last.setDate(last.getDate() + daysCount - 1);
    return last;
  }, [daysCount]);

  // Helper function to format a list with "and" before the last item
  const formatListLang = (items) => {
    return new Intl.ListFormat('en', { style: 'long', type: 'conjunction' }).format(items);
  };

  // Helper to format date as YYYY-MM-DD in local time
  const formatDateLocal = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  // Transform API timeslot data for all services
  const transformTimeslotDataForServices = (apiTimeslots, requestedDays = 30) => {
    const schedulers = [];

    // Process each timeslot group (can contain multiple services with same availability)
    Object.values(apiTimeslots).forEach((serviceData) => {
      // Get ALL services in this timeslot group
      const servicesMap = serviceData.services || {};
      const serviceIds = Object.keys(servicesMap);
      const serviceNames = Object.values(servicesMap);

      // Create a combined service name for display
      const combinedServiceName = formatListLang(serviceNames);

      // Extract timeslots - keep as-is from API
      const timeSlots = serviceData.timeslots || [];

      // Extract available slots and convert to the format scheduler expects
      // Also create a map to store the full slot data including extra
      const availableSet = new Set();
      const slotDataMap = {};
      (serviceData.available || []).forEach(slot => {
        // Convert new format: {date: "2026-01-27", timeslot: "MORNING APPT", extra: {...}}
        // to old format: "2026-01-27:MORNING APPT"
        const slotKey = `${slot.date}:${slot.timeslot}`;
        availableSet.add(slotKey);
        // Store the full slot data including extra
        slotDataMap[slotKey] = slot;
      });

      // Calculate unavailable slots - mark ALL slots not in available as unavailable
      // This ensures only dates returned by the API are selectable
      const unavailable = [];
      const startDate = new Date();

      for (let i = 0; i < requestedDays; i++) {
        const currentDate = new Date(startDate);
        currentDate.setDate(startDate.getDate() + i);
        // Use local date formatting instead of UTC
        const dateString = formatDateLocal(currentDate);

        // Check each timeslot for this date
        timeSlots.forEach(timeSlot => {
          const slotKey = `${dateString}:${timeSlot}`;
          if (!availableSet.has(slotKey)) {
            unavailable.push(slotKey);
          }
        });
      }

      schedulers.push({
        serviceIds,
        serviceNames,
        serviceName: combinedServiceName,
        servicesMap,
        timeSlots,
        unavailable,
        available: Array.from(availableSet),
        slotDataMap
      });
    });

    return schedulers;
  };

  const handleSlotSelected = (serviceIds, servicesMap, slotDataMap) => (slotData) => {
    // Look up the full slot data including extra
    const slotKey = `${slotData.dateString}:${slotData.timeSlot}`;
    const fullSlotData = slotDataMap[slotKey];

    // Create ONE appointment for ALL services in this group
    const appointmentData = {
      serviceIds: [...serviceIds],
      services: servicesMap,
      timeslot: {
        date: slotData.dateString,
        timeslot: slotData.timeSlot,
        dateObj: slotData.date,
        extra: fullSlotData?.extra || null
      }
    };

    // Store the appointment with a unique key based on all service IDs
    const appointmentKey = serviceIds.sort().join('-');
    setAppointmentForService(appointmentKey, appointmentData);
  };

  const handleNext = () => {
    // Check if all services have appointments scheduled
    if (canProceed() && onNext) {
      onNext();
    }
  };

  // Check if all services that require scheduling have appointments
  const canProceed = () => {
    // If there are no schedulers, only allow proceeding when scheduling was not
    // required (e.g. off site area rug cleaning). If scheduling was required but
    // no appointment times came back, the user must not proceed.
    if (serviceSchedulers.length === 0) {
      return !schedulingRequired;
    }

    // Flatten all service IDs from all appointments
    const scheduledServiceIds = new Set();
    scheduledAppointments.forEach(appointment => {
      // Safety check: ensure appointment has new structure
      if (appointment.serviceIds && Array.isArray(appointment.serviceIds)) {
        appointment.serviceIds.forEach(serviceId => {
          scheduledServiceIds.add(String(serviceId));
        });
      }
    });

    // Get all service IDs that require scheduling (from serviceSchedulers)
    const serviceIdsRequiringScheduling = new Set();
    serviceSchedulers.forEach(scheduler => {
      scheduler.serviceIds.forEach(serviceId => {
        serviceIdsRequiringScheduling.add(String(serviceId));
      });
    });

    // Check if all services that require scheduling have been scheduled
    return Array.from(serviceIdsRequiringScheduling).every(serviceId =>
      scheduledServiceIds.has(serviceId)
    );
  };

  return (
    <>
      {isTransitioning && <Loader message="Looking for available times..." />}
      <FormStepLayout
        onBack={onBack}
        onNext={handleNext}
        nextDisabled={!canProceed()}
        nextText="Next: Your Information"
        backText="Back"
        leftSidebar={true}
        rightSidebar={<RightSidebar showAppointmentDetails={true} />}
        title="Schedule Your Services"
      >
        {unavailabilityWarning && (
          <Alert
            type="warning"
            title="Appointment Times Changed"
            onClose={() => setUnavailabilityWarning(null)}
            leftAccent
          >
            {unavailabilityWarning}
          </Alert>
        )}

        {!isTransitioning && serviceSchedulers.length > 1 && (
          <Alert
            type="info"
            title="Why do I need to schedule multiple appointment times?"
            leftAccent
          >
            Due to the services you requested, a specialty truck is needed.
          </Alert>
        )}

        {!isTransitioning && serviceSchedulers.length === 0 && (
          <div className="text-center py-8">
            <p className="text-gray-600">
              No appointment times are available online at the moment.
            </p>
            {schedulingRequired && (
              <p className="text-gray-600 mt-2">
                Call{' '}
                <a href={`tel:${franchise?.phone?.telephone || '1-800-367-2648'}`}>
                  {franchise?.phone?.telephone || '1-800-367-2648'}
                </a>{' '}
                to schedule your services with our team.
              </p>
            )}
          </div>
        )}

        {!isTransitioning && serviceSchedulers.map((scheduler, index) => {
          // Get the stored appointment for any service in this group
          // All services in a group share the same timeslot, so we just need to find one
          const storedAppointment = scheduledAppointments.find(a => {
            // Safety check: ensure appointment has new structure
            if (!a.serviceIds || !Array.isArray(a.serviceIds)) return false;
            return scheduler.serviceIds.some(id => a.serviceIds.map(String).includes(String(id)));
          });

          // Memoize initial selected slot to prevent unnecessary recreations
          const initialSelectedSlot = useMemo(() => {
            if (!storedAppointment) return null;

            return {
              timeSlot: storedAppointment.timeslot.timeslot,
              date: storedAppointment.timeslot.dateObj instanceof Date
                ? storedAppointment.timeslot.dateObj
                : parseDateString(storedAppointment.timeslot.date),
              dateString: storedAppointment.timeslot.date
            };
          }, [storedAppointment?.timeslot?.timeslot, storedAppointment?.timeslot?.date]);

          // Create a unique key for this scheduler group
          const schedulerKey = scheduler.serviceIds.join('-');

          return (
            <div key={schedulerKey} className="mb-8">
              <div className="text-xl font-bold mb-4 text-gray-800">
                {scheduler.serviceName}
              </div>
              <AppointmentScheduler
                key={`scheduler-${schedulerKey}`}
                startDate={schedulerStartDate}
                daysCount={7}
                maxTotalDays={daysCount}
                maxDate={maxSelectableDate}
                timeSlots={scheduler.timeSlots}
                unavailable={scheduler.unavailable}
                initialSelectedSlot={initialSelectedSlot}
                onSlotSelected={handleSlotSelected(scheduler.serviceIds, scheduler.servicesMap, scheduler.slotDataMap)}
                text={{
                  headerTitle: 'CHOOSE YOUR APPOINTMENT TIME',
                  arrivalWindowsTitle: 'TIME SLOTS',
                  selectedAppointmentLabel: 'Your Booking:'
                }}
              />
            </div>
          );
        })}
      </FormStepLayout>
    </>
  );
}
